import * as React from 'react';
import { Collapse, CollapseProps } from 'bento';
import { SidebarNavCollapseHeader } from './SidebarNavCollapseHeader';
import { SidebarNavHeader } from './SidebarNavHeader';
import { SidebarNavItem } from './SidebarNavItem';
import { Theme, css } from '@emotion/react';

export type SidebarNavProps = {
  'aria-label': string;
} & SidebarNavNestedProps &
  React.HTMLAttributes<'HTMLNavElement'>;

type SidebarNavNestedProps =
  | { nested?: false; headerElement?: never; expanded?: never }
  | {
      nested: true;
      headerElement: CollapseProps['headerElement'];
      expanded?: boolean;
    };

const sidebarNav = ({ spacing }: Theme, { nested }: Omit<SidebarNavProps, 'aria-label'>) => css`
  ${nested ? `padding: ${spacing(1, 0, 0, 4)}` : `padding: ${spacing(0, 1, 0, 1)}`}
`;

const sidebarNavList = ({ spacing }: Theme) => css`
  display: grid;
  list-style-type: none;
  gap: ${spacing(1)}px;
  max-width: 240px;
`;

const sidebarNavListItem = ({ borderRadius }: Theme) => css`
  & > a {
    text-decoration: none;
    border-radius: ${borderRadius[100]};
    outline-offset: 3px;
    box-sizing: border-box;
  }
`;

type OptionalWrapperProps = {
  isWrapped: boolean;
  children: React.ReactNode;
  headerElement?: CollapseProps['headerElement'];
  expanded?: boolean;
};

const OptionalWrapper = ({
  isWrapped,
  headerElement,
  children,
  expanded,
  ...rest
}: OptionalWrapperProps) => {
  if (isWrapped && headerElement) {
    // Update header element to wrap with button and apply the internal dropdown icon
    // TODO: Have prop open this so that route matching can open this menu
    return (
      <Collapse headerElement={headerElement} expanded={expanded} {...rest}>
        <ul>{children}</ul>
      </Collapse>
    );
  }
  return <>{children}</>;
};

/**
 * MyComponent description
 * @param {Object} props - The props object
 * @param {string} props.title - The title of the component
 * @param {number} props.value - The value of the component
 * @param {function} props.onChange - Function called when the value changes
 * @returns {JSX.Element} React component
 */
function Root({
  children,
  'aria-label': ariaLabel,
  nested,
  headerElement,
  className,
  expanded,
  ...rest
}: SidebarNavProps) {
  function cloneThroughFragments(children: React.ReactNode): React.ReactNode {
    return React.Children.map(children, (c, i) => {
      if (React.isValidElement(c) && typeof c.type !== 'string') {
        return (
          <li key={i} css={(theme: Theme) => sidebarNavListItem(theme)} aria-selected={expanded}>
            {React.cloneElement(c as React.ReactElement, {})}
          </li>
        );
      }
      return c;
    });
  }
  const childrenToRender = cloneThroughFragments(children);

  return (
    <OptionalWrapper
      isWrapped={!!nested}
      headerElement={headerElement}
      expanded={expanded}
      {...rest}
    >
      <nav
        aria-label={ariaLabel}
        css={(theme: Theme) => sidebarNav(theme, { nested })}
        className={className}
      >
        <ul aria-label={ariaLabel} css={sidebarNavList}>
          {childrenToRender}
        </ul>
      </nav>
    </OptionalWrapper>
  );
}

/**
 * @deprecated Deprecated — use pantry Navigation instead.
 * @see https://docs.tocktix.com/pantry?path=/docs/components-navigation--docs
 */
export const SidebarNav = Object.assign(Root, {
  Item: SidebarNavItem,
  Header: SidebarNavHeader,
  CollapseHeader: SidebarNavCollapseHeader,
});
